import React, { ComponentProps } from "react";

export interface ServiceProviderProps<State = void> {
  initialState?: State;
  children: React.ReactNode;
}

export interface ServiceContainer<Service, State = void> {
  (): Service;
  Provider: React.ComponentType<ServiceProviderProps<State>>;
  FakeProvider: (fake: Service) => React.ComponentType<ComponentProps<any>>;
}

export function registerService<Service, State = void>(
  useHook: (initialState?: State) => Service
): ServiceContainer<Service, State> {
  let Context = React.createContext<Service | null>(null);

  function Provider(props: ServiceProviderProps<State>) {
    let value = useHook(props.initialState);
    return <Context.Provider value={value}>{props.children}</Context.Provider>;
  }

  function FakeProvider(fake: Service) {
    return (props: ComponentProps<any>) => (
      <Context.Provider value={fake}>{props.children}</Context.Provider>
    );
  }

  function useService(): Service {
    let value = React.useContext(Context);
    if (value === null) {
      throw new Error("The service has no provider");
    }
    return value;
  }

  useService.Provider = Provider;
  useService.FakeProvider = FakeProvider;
  return useService;
}

export function registerGlobalService<Service, State = void>(
  service: Service
): ServiceContainer<Service, State> {
  let Context = React.createContext<Service | null>(null);

  function Provider(props: ServiceProviderProps<State>) {
    return <Context.Provider value={service}>{props.children}</Context.Provider>;
  }

  function FakeProvider(fake: Service) {
    return (props: ComponentProps<any>) => (
      <Context.Provider value={fake}>{props.children}</Context.Provider>
    );
  }

  function useGlobalService(): Service {
    let value = React.useContext(Context);
    if (value === null) {
      throw new Error("The service has no provider");
    }
    return value;
  }

  useGlobalService.Provider = Provider;
  useGlobalService.FakeProvider = FakeProvider;
  return useGlobalService;
}
